import { useState } from 'react';
import { InvoiceService } from 'api';
import { isSuccessful } from 'api/utils';
import { useAuth } from 'contexts/authContext';
import { FetchInvoiceData } from 'pages/invoices/components/InvoiceDetails/InvoiceDetails.types';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { FaShareSquare, FaBan, FaRedoAlt } from 'react-icons/fa';
import { ImCheckmark } from 'react-icons/im';
import { IoMdPrint, IoMdMail, IoMdFlag, IoMdDownload } from 'react-icons/io';
import {
  canEditInvoice,
  canSendMail,
  isKsefInvoiceClass,
} from 'utils/invoiceFunctions.utils';
import { appToast } from 'utils/toast.utils';
import { ClearCorrectionDialog } from '../ClearCorrectionDialog';
import { PublishDialog } from '../PublishDialog';
import { RevertPublicationDialog } from '../RevertPublicationDialog';
import { InvoiceSendMailDialog } from './InvoiceSendMailDialog';
import * as S from './OptionsMenu.css';

type OptionsMenuProps = {
  invoice: Invoice | CorrectiveInvoice;
  isOptionsOpen: boolean;
  clickAwayHandler: () => void;
  anchorEl: HTMLElement | null;
  isCorrective: boolean;
  isOwn: boolean;
  requiresCorrection: boolean;
  invoicePublished: boolean;
  isPaid: boolean;
  canBeUnpublished: boolean;
  canBePublished: boolean;
  canPublish: boolean;
  canChangeStatus: boolean;
  canPrint: boolean;
  refetchInvoiceData: FetchInvoiceData;
};

export const OptionsMenu = ({
  invoice,
  isOptionsOpen,
  clickAwayHandler,
  anchorEl,
  isOwn,
  requiresCorrection,
  isCorrective,
  invoicePublished,
  isPaid,
  canBeUnpublished,
  canPublish,
  canBePublished,
  canChangeStatus,
  canPrint,
  refetchInvoiceData,
}: OptionsMenuProps) => {
  const { t } = useTranslation(['misc', 'invoice']);

  const { user, allowedReplacementsByCode } = useAuth();
  const sendsToKsef = isKsefInvoiceClass(invoice.invoiceClass);

  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [publishLang, setPublishLang] = useState<'pl' | 'en'>('pl');
  const [isClearCorrectionDialogOpen, setIsClearCorrectionDialogOpen] =
    useState(false);
  const [isRevertPublicationDialogOpen, setIsRevertPublicationDialogOpen] =
    useState(false);

  const hasUnrecognizedActivity = isCorrective
    ? (invoice as CorrectiveInvoice).correctedInvoiceLines.some(
        (l) => l.activity.code === 'NIEROZPOZNANE',
      ) ||
      (invoice as CorrectiveInvoice).previousInvoiceLines.some(
        (l) => l.activity.code === 'NIEROZPOZNANE',
      )
    : invoice.invoiceLines.some((l) => l.activity.code === 'NIEROZPOZNANE');

  const { publishInvoice } = InvoiceService.usePublishInvoice();
  const { changeInvoiceStatus } = InvoiceService.useChangeInvoiceStatus();
  const { printInvoice } = InvoiceService.usePrintInvoice();
  const { downloadInvoiceUpo, loading: upoLoading } =
    InvoiceService.useDownloadInvoiceUpo();
  const { clearInvoiceCorrectionRequired } =
    InvoiceService.useClearInvoiceCorrectionRequired();
  const { revertInvoicePublication } =
    InvoiceService.useRevertInvoicePublication();

  const handlePublishInvoice = async () => {
    const res = await publishInvoice(invoice.id, publishLang, isCorrective);
    if (isSuccessful(res)) {
      appToast.success(
        t(
          res.data.result?.outcome === 'ACCEPTED'
            ? 'publishKsefSuccess'
            : 'publishSuccess',
          { ns: 'invoice' },
        ),
      );
    }
    clickAwayHandler();
    await refetchInvoiceData(invoice.id, isCorrective);
  };

  const handleOpenPublishDialog = (lang: 'pl' | 'en') => () => {
    setPublishLang(lang);
    setIsPublishDialogOpen(true);
    clickAwayHandler();
  };

  const handleChangeInvoiceStatus = async () => {
    clickAwayHandler();
    const res = await changeInvoiceStatus(
      [invoice.id],
      isCorrective,
      isPaid ? false : true,
    );
    isSuccessful(res) &&
      appToast.success(
        t(!isPaid ? 'paidSuccess' : 'unpaidSuccess', { ns: 'invoice' }),
      );
    await refetchInvoiceData(invoice.id, isCorrective);
  };

  const handleClearInvoiceCorrection = async () => {
    const res = await clearInvoiceCorrectionRequired(invoice.id);
    isSuccessful(res) &&
      appToast.success(t('clearedRequiresCorrectionMark', { ns: 'invoice' }));
    await refetchInvoiceData(invoice.id, isCorrective);
  };

  const handleOpenClearCorrectionDialog = () => {
    clickAwayHandler();
    setIsClearCorrectionDialogOpen(true);
  };

  const handlePrintInvoice = async (lang: 'pl' | 'en') => {
    clickAwayHandler();
    await printInvoice(invoice.id, isCorrective, lang);
  };

  const handleDownloadUpo = async () => {
    if (!invoice.ksefNumber || upoLoading) return;

    clickAwayHandler();
    try {
      await downloadInvoiceUpo(invoice.id, invoice.ksefNumber, isCorrective);
    } catch {
      appToast.error(t('downloadUpoError', { ns: 'invoice' }));
    }
  };

  const handleRevertPublication = async () => {
    clickAwayHandler();
    const res = await revertInvoicePublication(isCorrective, invoice.id);
    isSuccessful(res) &&
      appToast.success(t('unpublishSuccess', { ns: 'invoice' }));
    await refetchInvoiceData(invoice.id, isCorrective);
  };

  const handleOpenRevertPublicationDialog = () => {
    clickAwayHandler();
    setIsRevertPublicationDialogOpen(true);
  };

  // send mail

  const [isMailDialogOpen, setIsMailDialogOpen] = useState(false);

  const handleOpenMailDialog = () => {
    setIsMailDialogOpen(true);
    clickAwayHandler();
  };

  const handleCloseMailDialog = () => {
    setIsMailDialogOpen(false);
  };

  return (
    <>
      <PublishDialog
        open={isPublishDialogOpen}
        onClose={() => setIsPublishDialogOpen(false)}
        onSubmit={handlePublishInvoice}
        isOwn={isOwn}
        sendsToKsef={sendsToKsef}
        invoiceCode={invoice.code}
        language={publishLang}
      />
      <ClearCorrectionDialog
        open={isClearCorrectionDialogOpen}
        onClose={() => setIsClearCorrectionDialogOpen(false)}
        onSubmit={handleClearInvoiceCorrection}
      />
      <RevertPublicationDialog
        open={isRevertPublicationDialogOpen}
        onClose={() => setIsRevertPublicationDialogOpen(false)}
        onSubmit={handleRevertPublication}
      />
      <S.StyledMenu
        id='options-menu'
        anchorEl={anchorEl}
        open={isOptionsOpen}
        onClose={clickAwayHandler}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {(isCorrective ? canPublish : canBePublished && canPublish) &&
          !hasUnrecognizedActivity &&
          (isOwn ? (
            <div>
              <S.StyledMenuItem onClick={handleOpenPublishDialog('pl')}>
                <FaShareSquare size={18} />
                <S.StyledMenuLabel>
                  {sendsToKsef
                    ? t('publishToKsef', { ns: 'invoice', language: 'PL' })
                    : `${t('button.publish', { ns: 'misc' })} PL`}
                </S.StyledMenuLabel>
              </S.StyledMenuItem>
              <S.StyledMenuItem onClick={handleOpenPublishDialog('en')}>
                <FaShareSquare size={18} />
                <S.StyledMenuLabel>
                  {sendsToKsef
                    ? t('publishToKsef', { ns: 'invoice', language: 'EN' })
                    : `${t('button.publish', { ns: 'misc' })} EN`}
                </S.StyledMenuLabel>
              </S.StyledMenuItem>
            </div>
          ) : (
            <S.StyledMenuItem onClick={handleOpenPublishDialog('pl')}>
              <FaShareSquare size={18} />
              <S.StyledMenuLabel>
                {t('button.publish', { ns: 'misc' })}
              </S.StyledMenuLabel>
            </S.StyledMenuItem>
          ))}

        {canChangeStatus && (
          <S.StyledMenuItem onClick={handleChangeInvoiceStatus}>
            {!isPaid ? (
              <>
                <ImCheckmark size={16} />
                <S.StyledMenuLabel>
                  {t('invoiceStatus.PAID', { ns: 'invoice' })}
                </S.StyledMenuLabel>
              </>
            ) : (
              <>
                <FaBan size={18} />
                <S.StyledMenuLabel>
                  {t('invoiceStatus.NOT_PAID', { ns: 'invoice' })}
                </S.StyledMenuLabel>
              </>
            )}
          </S.StyledMenuItem>
        )}

        {!isCorrective &&
          requiresCorrection &&
          canEditInvoice(invoice, user, allowedReplacementsByCode) && (
            <S.StyledMenuItem onClick={handleOpenClearCorrectionDialog}>
              <IoMdFlag size={18} />
              <S.StyledMenuLabel>
                {t('clearCorrectionRequired', { ns: 'invoice' })}
              </S.StyledMenuLabel>
            </S.StyledMenuItem>
          )}

        {isOwn &&
          canPrint &&
          (canBePublished ? (
            <div>
              <S.StyledMenuItem onClick={() => handlePrintInvoice('pl')}>
                <IoMdPrint size={18} />
                <S.StyledMenuLabel>
                  {`${t('button.print', { ns: 'misc' })} ${t('languages.PL', {
                    ns: 'misc',
                  })}`}
                </S.StyledMenuLabel>
              </S.StyledMenuItem>
              <S.StyledMenuItem onClick={() => handlePrintInvoice('en')}>
                <IoMdPrint size={18} />
                <S.StyledMenuLabel>
                  {`${t('button.print', { ns: 'misc' })} ${t('languages.GB', {
                    ns: 'misc',
                  })}`}
                </S.StyledMenuLabel>
              </S.StyledMenuItem>
            </div>
          ) : (
            <S.StyledMenuItem onClick={() => handlePrintInvoice('pl')}>
              <IoMdPrint size={18} />
              <S.StyledMenuLabel>
                {t('button.print', { ns: 'misc' })}
              </S.StyledMenuLabel>
            </S.StyledMenuItem>
          ))}

        {invoice.ksefNumber && (
          <S.StyledMenuItem onClick={handleDownloadUpo} disabled={upoLoading}>
            <IoMdDownload size={18} />
            <S.StyledMenuLabel>
              {t('downloadUpo', { ns: 'invoice' })}
            </S.StyledMenuLabel>
          </S.StyledMenuItem>
        )}

        {invoicePublished &&
          canSendMail(invoice, user, allowedReplacementsByCode) && (
            <S.StyledMenuItem onClick={handleOpenMailDialog}>
              <IoMdMail size={18} />
              <S.StyledMenuLabel>
                {t('button.mail', { ns: 'misc' })}
              </S.StyledMenuLabel>
            </S.StyledMenuItem>
          )}

        {canBeUnpublished && (
          <S.StyledMenuItem onClick={handleOpenRevertPublicationDialog}>
            <FaRedoAlt size={18} />
            <S.StyledMenuLabel>
              {t('unpublish', { ns: 'invoice' })}
            </S.StyledMenuLabel>
          </S.StyledMenuItem>
        )}
      </S.StyledMenu>

      <InvoiceSendMailDialog
        invoice={invoice}
        isOpen={isMailDialogOpen}
        onClose={handleCloseMailDialog}
      />
    </>
  );
};
